import { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button, LinkButton } from './Button';
import Logo from './Logo';

const navLinks = [
  { name: 'Home', to: '/' },
  { name: 'Investments', to: '/investments' },
  { name: 'How It Works', to: '/how-it-works' },
  {
    name: 'About',
    subLinks: [
      { name: 'Why ALTA-X', to: '/why-altax' },
      { name: 'About Us', to: '/about' },
    ],
  },
  { name: 'Education', to: '/education' },
  { name: 'News', to: 'http://news.alta-x.com/', external: true },
];

const Dropdown = ({ link, closeMenu = () => {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Handle hover with delay to prevent accidental closing
  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  return (
    <div 
      className="relative" 
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button 
        className={`flex items-center text-base font-medium transition-colors duration-200 px-3 py-2 ${
          isOpen 
            ? 'text-[hsl(var(--navy-dark))] font-semibold' 
            : 'text-gray-700 hover:text-[hsl(var(--navy-dark))]'
        }`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {link.name}
        <ChevronDown 
          className={`h-4 w-4 ml-1 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>
      
      <div 
        className={`absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 z-50 transition-all duration-200 origin-top-left ${
          isOpen 
            ? 'opacity-100 scale-100' 
            : 'opacity-0 scale-95 pointer-events-none'
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="py-1">
          {link.subLinks.map((subLink) => (
            <NavLink
              key={subLink.name}
              to={subLink.to}
              onClick={() => {
                setIsOpen(false);
                if (closeMenu) closeMenu();
              }}
              className={({ isActive }) =>
                `block px-4 py-2.5 text-sm transition-colors duration-200 ${
                  isActive 
                    ? 'bg-[hsl(var(--navy-light))]/10 text-[hsl(var(--navy-dark))] font-medium' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              <div className="flex items-center">
                <span>{subLink.name}</span>
                {subLink.name === 'Why ALTA-X' && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-[hsl(var(--bronze-gold))]/10 text-[hsl(var(--bronze-gold))]">
                    New
                  </span>
                )}
              </div>
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Add scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white/90'}`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo and Navigation Container */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0">
              <Logo className="h-10 w-auto text-[hsl(var(--navy-dark))]" />
            </Link>
            
            {/* Desktop Navigation - Left Aligned */}
            <nav className="hidden lg:flex items-center space-x-8 ml-10">
              {navLinks.map((link) =>
                link.subLinks ? (
                  <Dropdown key={link.name} link={link} closeMenu={() => setIsMenuOpen(false)} />
                ) : (
                  link.external ? (
                    <a
                      key={link.name}
                      href={link.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`relative px-3 py-2 text-base font-medium transition-colors duration-200 text-gray-700 hover:text-[hsl(var(--navy-dark))]`}
                    >
                      {link.name}
                    </a>
                  ) : (
                    <NavLink
                      key={link.name}
                      to={link.to}
                      className={({ isActive }) =>
                        `relative px-3 py-2 text-base font-medium transition-colors duration-200 ${
                          isActive 
                            ? 'text-[hsl(var(--navy-dark))] font-semibold' 
                            : 'text-gray-700 hover:text-[hsl(var(--navy-dark))]'
                        }`
                      }
                      children={({ isActive }) => (
                        <>
                          {link.name}
                          {isActive && (
                            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[hsl(var(--bronze-gold))]" />
                          )}
                        </>
                      )}
                    />
                  )
                )
              )}
            </nav>
          </div>

          {/* Desktop Action Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="https://app.alta-x.com"
              className="inline-flex items-center justify-center rounded-full font-semibold tracking-wide transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 px-5 py-2 text-base border-2 border-navy/20 text-navy hover:bg-navy/5 hover:text-navy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Log In
            </a>
            <a
              href="https://app.alta-x.com/signup"
              className="inline-flex items-center justify-center rounded-full font-semibold tracking-wide transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 px-5 py-2 text-base bg-[hsl(var(--navy-dark))] hover:bg-[hsl(var(--navy))] text-white"
              target="_blank"
              rel="noopener noreferrer"
            >
              Sign Up
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Button 
              variant="ghost" 
              onClick={() => setIsMenuOpen(!isMenuOpen)} 
              className="p-2 text-gray-700 hover:bg-gray-100 rounded-full"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`lg:hidden transition-all duration-300 ease-in-out overflow-hidden ${
          isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 py-3 space-y-1 bg-white border-t border-gray-200">
          {navLinks.map((link) =>
            link.subLinks ? (
              <div key={link.name} className="mb-2">
                <button
                  onClick={() => {
                    const subMenu = document.getElementById(`mobile-submenu-${link.name}`);
                    if (subMenu) {
                      subMenu.classList.toggle('hidden');
                    }
                  }}
                  className="w-full flex items-center justify-between px-3 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <span>{link.name}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200`} />
                </button>
                <div id={`mobile-submenu-${link.name}`} className="hidden pl-4 mt-1 space-y-1">
                  {link.subLinks.map((subLink) => (
                    <NavLink
                      key={subLink.name}
                      to={subLink.to}
                      onClick={() => setIsMenuOpen(false)}
                      className={({ isActive }) =>
                        `block px-3 py-2.5 text-base font-medium rounded-lg transition-colors ${
                          isActive
                            ? 'bg-[hsl(var(--navy-light))]/10 text-[hsl(var(--navy-dark))]'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`
                      }
                    >
                      <div className="flex items-center">
                        <span>{subLink.name}</span>
                        {subLink.name === 'Why ALTA-X' && (
                          <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-[hsl(var(--bronze-gold))]/10 text-[hsl(var(--bronze-gold))]">
                            New
                          </span>
                        )}
                      </div>
                    </NavLink>
                  ))}
                </div>
              </div>
            ) : (
              link.external ? (
                <a
                  key={link.name}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-3 py-3 text-base font-medium rounded-lg transition-colors text-gray-700 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </a>
              ) : (
                <NavLink
                  key={link.name}
                  to={link.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-3 text-base font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-[hsl(var(--navy-light))]/10 text-[hsl(var(--navy-dark))] font-semibold'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`
                  }
                >
                  {link.name}
                </NavLink>
              )
            )
          )}
          
          {/* Mobile Action Buttons */}
          <div className="pt-3 mt-2 border-t border-gray-100">
            <div className="space-y-3">
              <a
                href="https://app.alta-x.com"
                className="w-full flex items-center justify-center rounded-full font-semibold tracking-wide transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 px-6 py-3 text-base border-2 border-navy/20 text-navy hover:bg-navy/5 hover:text-navy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
              >
                Log In
              </a>
              <a
                href="https://app.alta-x.com/signup"
                className="w-full flex items-center justify-center rounded-full font-semibold tracking-wide transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 px-6 py-3 text-base bg-[hsl(var(--navy-dark))] hover:bg-[hsl(var(--navy))] text-white"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
              >
                Sign Up
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
